export const PRIVACY_POLICY_VERSION = "2026-06";
export const PRIVACY_CONTACT = "privacy@talentahela.com";

export const PRIVACY_POLICY = {
  title: "Privacy Policy",
  subtitle: "How Talanta Hela and One Talanta ID handle your personal data under the Kenya Data Protection Act, 2019.",
  updated: "Last updated: June 2026 · Policy version " + PRIVACY_POLICY_VERSION,
  sections: [
    {
      title: "1. Who we are (Data Controller)",
      body: [
        "Talanta Hela is a Government of Kenya programme delivered under the Ministry of Youth Affairs, Creative Economy and Sports together with the Ministry of Information, Communications and the Digital Economy. For the purposes of the Data Protection Act, 2019 (\"the Act\"), Talanta Hela is the data controller of the personal data processed through talentahela.com and One Talanta ID.",
        `Data protection enquiries: ${PRIVACY_CONTACT}.`,
      ],
    },
    {
      title: "2. What we collect",
      bullets: [
        "Identity & contact: your mobile number (verified by SMS one-time code), and optionally your name, email address and county of residence.",
        "Account data: your One Talanta ID number, role, linked-athlete reference (if you connect an existing Talanta Sport profile), consent record and timestamps.",
        "Security & session data: IP address, device/browser user-agent, session creation and expiry times, and security events (code requests, failed attempts, logins, logouts).",
        "Pillar interest: the pillar you asked to be notified about and the contact you provided.",
        "We do not collect passwords, national ID numbers, biometric data or payment card data at this stage.",
      ],
    },
    {
      title: "3. Why we process your data & lawful basis (Section 30)",
      bullets: [
        "To create, verify and secure your One Talanta ID account — performance of a contract with you and your consent (given when you accept this policy).",
        "To protect the platform against fraud, abuse and unauthorised access (rate limiting, audit trail) — our legitimate interest in keeping a national platform safe.",
        "To notify you about the launch of pillars you subscribed to — your consent, which you can withdraw at any time.",
        "To enable single sign-on with trusted partners such as Talanta Sport (NextGen) — only when you initiate the connection.",
      ],
    },
    {
      title: "4. Sharing & transfers (Sections 48–50)",
      body: [
        "We share only the minimum data needed with processors acting on our instructions: an SMS gateway to deliver your verification code, and our cloud hosting provider. When you choose to link or hand off to Talanta Sport, a signed identity token containing your One Talanta ID, name and phone number is shared with that service.",
        "Where a processor stores data outside Kenya, we ensure appropriate safeguards are in place as required by the Act and the Data Protection (General) Regulations, 2021.",
        "We never sell personal data.",
      ],
    },
    {
      title: "5. Retention (Section 39)",
      bullets: [
        "Account & profile data: for as long as your One Talanta ID is active. Deleted within 30 days of an account deletion request.",
        "One-time codes: expire after 5 minutes and are automatically purged shortly after.",
        "Sign-in sessions: 30 days, or immediately upon sign-out.",
        "Security event logs: 180 days, then automatically deleted.",
      ],
    },
    {
      title: "6. Your rights (Section 26)",
      body: ["You have the right to:"],
      bullets: [
        "Be informed about how your data is used — this policy.",
        "Access and receive a copy of your data — use \"Download my data\" in your One Talanta ID profile.",
        "Correct inaccurate or incomplete data — edit your profile at any time.",
        "Erase your data — use \"Delete my account\" in your profile, or write to us.",
        "Object to or restrict processing, and withdraw consent — contact us; withdrawing consent may mean we cannot keep your account open.",
        "Not be subject to a decision based solely on automated processing.",
      ],
      after: [
        `We respond to rights requests within 14 days as required by the Act. To exercise a right, use the tools in your profile or email ${PRIVACY_CONTACT}.`,
      ],
    },
    {
      title: "7. Security (Section 41)",
      body: [
        "One Talanta ID is passwordless. Verification codes are hashed before storage and compared in constant time; failed attempts are limited and requests are rate-limited per number and per network address. Access tokens are short-lived (15 minutes) and signed with RS256; refresh tokens are opaque, stored hashed and rotated on every use. All traffic is encrypted in transit (HTTPS/TLS).",
      ],
    },
    {
      title: "8. Children",
      body: [
        "Talanta Hela serves young Kenyans, including minors who participate in talent programmes. Where a user is under 18, processing is carried out in the best interests of the child and we may require verification of parental or guardian consent in line with Section 33 of the Act.",
      ],
    },
    {
      title: "9. Complaints",
      body: [
        "If you believe your data has been mishandled, please contact us first so we can put it right. You also have the right to lodge a complaint with the Office of the Data Protection Commissioner (ODPC), Kenya — www.odpc.go.ke.",
      ],
    },
    {
      title: "10. Changes",
      body: [
        "We will post updates here and, for material changes, ask you to re-accept the policy the next time you sign in. The version you accepted is recorded on your account.",
      ],
    },
  ],
};

export const TERMS = {
  title: "Terms of Use",
  subtitle: "The rules for using talentahela.com and your One Talanta ID.",
  updated: "Last updated: June 2026",
  sections: [
    {
      title: "1. Acceptance",
      body: [
        "By creating a One Talanta ID or using talentahela.com you agree to these Terms and to our Privacy Policy. If you are under 18, a parent or guardian must agree on your behalf.",
      ],
    },
    {
      title: "2. Your One Talanta ID",
      bullets: [
        "Your account is personal. Keep your phone secure and never share verification codes — Talanta Hela staff will never ask for them.",
        "You must provide accurate information and keep it up to date.",
        "One person, one ID. Duplicate or impersonating accounts may be suspended.",
      ],
    },
    {
      title: "3. Acceptable use",
      bullets: [
        "Do not attempt to access other users' accounts, probe or overload the service, or bypass security controls.",
        "Do not use the platform for unlawful, abusive, fraudulent or discriminatory purposes.",
        "Content you submit must be yours or licensed to you and must not infringe others' rights.",
      ],
    },
    {
      title: "4. Pillars & partner services",
      body: [
        "Sport, Digital, Soko, Education, Gaming and Broadcast pillars are delivered by Talanta Hela and its partners (for example Talanta Sport powered by NextGen). Partner services may have additional terms presented when you connect to them. Availability of pillars may change as the programme rolls out.",
      ],
    },
    {
      title: "5. Suspension & termination",
      body: [
        "We may suspend or close accounts that breach these Terms or pose a security risk. You can delete your account at any time from your profile.",
      ],
    },
    {
      title: "6. Liability",
      body: [
        "The service is provided on an \"as is\" basis during the phased national rollout. To the extent permitted by Kenyan law, Talanta Hela is not liable for indirect or consequential losses arising from use of, or inability to use, the platform.",
      ],
    },
    {
      title: "7. Governing law",
      body: ["These Terms are governed by the laws of the Republic of Kenya and the courts of Kenya have jurisdiction."],
    },
    {
      title: "8. Contact",
      body: [`Questions about these Terms: ${PRIVACY_CONTACT}.`],
    },
  ],
};
